import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import PhotoCameraRoundedIcon from "@mui/icons-material/PhotoCameraRounded";
import { selectUser } from "../../features/userSlice";
import { PageHeader, PageShell } from "../../components/UI/Page";
import { EmptyState, LoadingState, StatTile } from "../../components/UI/States";
import { BackLink, BranchSelect, Notice, SearchField } from "../../components/Fleet/FleetParts";
import { relativeDay } from "../../components/Fleet/fleetFormat";
import { fetchSafetyConcerns } from "./safetyApi";
import { ConcernDialog, ConcernStatusChip, matchesQuery, pickBranch, plural, sameBranch, useUrlFilters } from "./SafetyParts";
import { SAFETY_PAGES } from "./safetyPages";
import { ReportActions, reportScope } from "./SafetyReport";

// Safety concerns reported from the mobile app — read-only here. Status
// changes and notes are made in the app (Safety Manager → Safety Submissions).

const PAGE = SAFETY_PAGES.concerns;

const STATUSES = {
  attention: { label: "Needs attention", hint: "Open or in review", tone: "rose", test: (c) => c.status !== "resolved" },
  open: { label: "Open", hint: "Not looked at yet", tone: "rose", test: (c) => c.status === "open" },
  in_review: { label: "In review", hint: "Being worked on", tone: "amber", test: (c) => c.status === "in_review" },
  resolved: { label: "Resolved", hint: "Closed out", tone: "emerald", test: (c) => c.status === "resolved" },
  all: { label: "All", hint: "Every concern", tone: "default", test: () => true },
};

const SafetyConcerns = () => {
  const user = useSelector(selectUser);
  const filters = useUrlFilters(Object.keys(STATUSES), "attention");
  const [concerns, setConcerns] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    fetchSafetyConcerns(user.id)
      .then((data) => setConcerns(data.concerns))
      .catch((e) => setError(e.message));
  }, [user?.id]);

  const branches = useMemo(
    () => [...new Set((concerns || []).map((c) => c.branch))].sort((a, b) => a.localeCompare(b)),
    [concerns]
  );
  const inBranch = (concerns || []).filter((c) => !filters.branch || sameBranch(c.branch, filters.branch));
  const visible = inBranch
    .filter(STATUSES[filters.status].test)
    .filter((c) => matchesQuery(filters.query, c.description, c.submitterName, c.branch));

  return (
    <PageShell size="lg">
      <ConcernDialog concern={open} userId={user?.id} onClose={() => setOpen(null)} />
      <BackLink to="/safety/overview">Safety Overview</BackLink>
      <PageHeader
        icon={PAGE.icon}
        tone={PAGE.tone}
        eyebrow="Safety"
        title={PAGE.name}
        description="Hazards people report from the Big C mobile app. Click one to see its photos and notes."
      />

      {error && <Notice tone="rose">{error}</Notice>}
      {!concerns && !error && <LoadingState label="Loading safety concerns…" />}

      {concerns && (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {Object.entries(STATUSES).map(([key, s]) => (
              <StatTile
                key={key}
                label={s.label}
                value={inBranch.filter(s.test).length}
                hint={s.hint}
                tone={s.tone}
                active={filters.status === key}
                onClick={() => filters.set("status", key)}
              />
            ))}
          </div>

          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <SearchField
              value={filters.query}
              onChange={(v) => filters.set("q", v)}
              placeholder="Search description, person, or branch…"
            />
            <BranchSelect
              value={pickBranch(branches, filters.branch)}
              onChange={(v) => filters.set("branch", v)}
              branches={branches}
            />
            <span className="app-muted text-sm tabular-nums sm:ml-auto">{plural(visible.length, "concern")}</span>
            <ReportActions
              kind="concerns"
              userId={user?.id}
              tone={PAGE.tone}
              disabled={!visible.length}
              selection={{
                ids: visible.map((c) => c.id),
                branch: pickBranch(branches, filters.branch),
                scope: reportScope({
                  branch: pickBranch(branches, filters.branch),
                  status: filters.status === "all" ? null : STATUSES[filters.status].label,
                  query: filters.query,
                }),
              }}
            />
          </div>

          <div className="app-card overflow-hidden rounded-2xl border">
            {visible.length === 0 ? (
              <EmptyState
                compact
                icon={PAGE.icon}
                title={filters.status === "attention" ? "No open safety concerns." : "No concerns match these filters."}
                description="New concerns show up here as soon as someone reports one in the mobile app."
              />
            ) : (
              <ul className="divide-y divide-[color:var(--app-divider)]">
                {visible.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => setOpen(item)}
                      className="flex w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-[color:var(--app-hover)] sm:px-5"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-2">
                          <ConcernStatusChip status={item.status} />
                          <span className="font-semibold">{item.branch}</span>
                          {item.photoCount > 0 && (
                            <span className="app-muted inline-flex items-center gap-0.5 text-xs">
                              <PhotoCameraRoundedIcon sx={{ fontSize: 14 }} />
                              {item.photoCount}
                            </span>
                          )}
                        </span>
                        <span className="mt-0.5 line-clamp-2 block text-sm">{item.description || "No description."}</span>
                        <span className="app-muted block text-xs">
                          {item.submitterName || "Someone"} · {relativeDay(item.createdAt)}
                          {item.noteCount ? ` · ${plural(item.noteCount, "note")}` : ""}
                        </span>
                      </span>
                      <ChevronRightRoundedIcon className="app-muted mt-1 shrink-0" fontSize="small" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <p className="app-muted mt-3 text-xs">
            To change a concern's status or add a note, use Safety Manager → Safety Submissions in the Big C mobile app.
          </p>
        </>
      )}
    </PageShell>
  );
};

export default SafetyConcerns;
